/* eslint-disable */
/**
 * Stripe payment helpers for FADED.
 * Keeps all Stripe.js logic out of the 9500-line App.js.
 *
 * Usage:
 *   import { mountCardElement, confirmCardBooking, destroyCardElement } from './stripePayment';
 */
import { loadStripe } from "@stripe/stripe-js";

// Initialise once — uses window.Stripe if preloaded in index.html (Capacitor native),
// otherwise falls back to loadStripe() dynamic injection.
let stripePromise = null;
export function getStripe() {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key) return null;
  if (!stripePromise) {
    if (window.Stripe) {
      stripePromise = Promise.resolve(window.Stripe(key));
    } else {
      stripePromise = loadStripe(key);
    }
  }
  return stripePromise;
}

export const STRIPE_AVAILABLE = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Card element styles that match FADED's dark theme
const CARD_STYLE = {
  base: {
    color: "#F5F5F0",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "15px",
    fontWeight: "600",
    "::placeholder": { color: "rgba(245,245,240,0.35)" },
    iconColor: "#2563EB",
  },
  invalid: { color: "#EF4444", iconColor: "#EF4444" },
};

let _stripe = null;
let _elements = null;
let _cardElement = null;

/**
 * Mount a Stripe card element into the given DOM node ref.
 * Call this when the user selects "Credit / Debit Card" on the payment step.
 * Returns a cleanup function — call it when the modal unmounts.
 */
export async function mountCardElement(containerRef) {
  const sp = getStripe();
  if (!sp || !containerRef) return () => {};

  _stripe = await sp;
  _elements = _stripe.elements();
  _cardElement = _elements.create("card", {
    style: CARD_STYLE,
    hidePostalCode: true,
  });
  _cardElement.mount(containerRef);

  return () => {
    try { _cardElement?.unmount(); } catch {}
    _cardElement = null;
    _elements = null;
  };
}

/**
 * Create a PaymentIntent via the edge function, then confirm the card payment.
 * Returns { success: true } or { success: false, error: string }.
 */
export async function confirmCardBooking({ supabase, bookingId, barberId, amountCents, tipCents = 0, giftCardId = null }) {
  if (!_stripe || !_cardElement) {
    return { success: false, error: "Card element not ready." };
  }

  // 1. Create PaymentIntent server-side
  const { data, error: fnError } = await supabase.functions.invoke("stripe-create-payment-intent", {
    body: { booking_id: bookingId, barber_id: barberId, amount_cents: amountCents, tip_cents: tipCents, gift_card_id: giftCardId },
  });

  if (fnError || !data?.client_secret) {
    return { success: false, error: data?.error ?? fnError?.message ?? "Failed to create payment." };
  }

  // 2. Confirm the card payment client-side (Stripe handles 3DS / SCA)
  try {
    const { error: stripeError } = await _stripe.confirmCardPayment(data.client_secret, {
      payment_method: { card: _cardElement },
    });

    if (stripeError) {
      return { success: false, error: stripeError.message };
    }
  } catch (e) {
    return { success: false, error: e?.message || "Card payment could not be completed — please try again." };
  }

  return { success: true, paymentIntentId: data.payment_intent_id };
}

/** Destroy the card element (call on modal unmount). */
export function destroyCardElement() {
  try { _cardElement?.unmount(); } catch {}
  _cardElement = null;
  _elements = null;
  _stripe = null;
}

// ─── Setup Intent helpers (no-show protection card-on-file) ───────────────────

let _setupStripe = null;
let _setupElements = null;
let _setupCardElement = null;

/** Mount a card element for SetupIntent (saving card without charging). */
export async function mountSetupCardElement(containerRef) {
  const sp = getStripe();
  if (!sp || !containerRef) return () => {};

  _setupStripe = await sp;
  _setupElements = _setupStripe.elements();
  _setupCardElement = _setupElements.create("card", {
    style: CARD_STYLE,
    hidePostalCode: true,
  });
  _setupCardElement.mount(containerRef);

  return () => {
    try { _setupCardElement?.unmount(); } catch {}
    _setupCardElement = null;
    _setupElements = null;
  };
}

/**
 * Create a SetupIntent via the edge function, then confirm it to save the card.
 * bookingId is optional — pass null when saving from the client profile page.
 * Returns { success: true, paymentMethodId, brand, last4 } or { success: false, error }.
 */
export async function confirmSetupIntent({ supabase, bookingId }) {
  if (!_setupStripe || !_setupCardElement) {
    return { success: false, error: "Card element not ready." };
  }

  const { data, error: fnError } = await supabase.functions.invoke("stripe-create-setup-intent", {
    body: { booking_id: bookingId },
  });

  if (fnError || !data?.client_secret) {
    return { success: false, error: data?.error ?? fnError?.message ?? "Failed to set up card." };
  }

  let setupIntent;
  try {
    const { setupIntent: si, error: stripeError } = await _setupStripe.confirmCardSetup(data.client_secret, {
      payment_method: { card: _setupCardElement },
    });
    if (stripeError) {
      return { success: false, error: stripeError.message };
    }
    setupIntent = si;
  } catch (e) {
    return { success: false, error: e?.message || "Card could not be saved — please try again." };
  }

  const pmId = setupIntent.payment_method;
  let brand = null, last4 = null;
  try {
    const { paymentMethod } = await _setupStripe.retrievePaymentMethod(pmId);
    brand = paymentMethod?.card?.brand ?? null;
    last4 = paymentMethod?.card?.last4 ?? null;
  } catch {}

  return { success: true, paymentMethodId: pmId, brand, last4 };
}

export function destroySetupCardElement() {
  try { _setupCardElement?.unmount(); } catch {}
  _setupCardElement = null;
  _setupElements = null;
  _setupStripe = null;
}

// ─── Payment Request Button (Apple Pay / Google Pay) ─────────────────────────

let _prStripe = null;
let _prElement = null;
let _prRequest = null;

/**
 * Mount a Payment Request Button (Apple Pay or Google Pay) into containerRef.
 * onPaymentMethod is called with the Stripe paymentmethod event when the user
 * authenticates — call ev.complete('success') or ev.complete('fail') on it.
 * Returns { canPay: bool, cleanup: fn }.
 */
export async function mountPaymentRequestButton(containerRef, { amountCents, serviceLabel, onPaymentMethod }) {
  const sp = getStripe();
  if (!sp || !containerRef) return { canPay: false, cleanup: () => {} };

  _prStripe = await sp;
  _prRequest = _prStripe.paymentRequest({
    country: 'CA',
    currency: 'cad',
    total: { label: `FADED — ${serviceLabel}`, amount: amountCents },
    requestPayerName: false,
    requestPayerEmail: false,
  });

  const result = await _prRequest.canMakePayment();
  if (!result) {
    _prStripe = null;
    _prRequest = null;
    return { canPay: false, cleanup: () => {} };
  }

  _prRequest.on('paymentmethod', onPaymentMethod);

  const prElements = _prStripe.elements();
  _prElement = prElements.create('paymentRequestButton', {
    paymentRequest: _prRequest,
    style: { paymentRequestButton: { type: 'default', theme: 'dark', height: '52px' } },
  });
  _prElement.mount(containerRef);

  return {
    canPay: result,
    cleanup: () => { destroyPaymentRequestButton(); },
  };
}

/**
 * Confirm a PaymentIntent using the payment method from the PR event.
 * Call this inside the onPaymentMethod handler.
 */
export async function confirmPaymentRequestBooking({ supabase, bookingId, barberId, amountCents, tipCents = 0, giftCardId = null, ev }) {
  if (!_prStripe) { ev.complete('fail'); return { success: false, error: 'Payment not ready.' }; }

  const { data, error: fnError } = await supabase.functions.invoke('stripe-create-payment-intent', {
    body: { booking_id: bookingId, barber_id: barberId, amount_cents: amountCents, tip_cents: tipCents, gift_card_id: giftCardId },
  });

  if (fnError || !data?.client_secret) {
    ev.complete('fail');
    return { success: false, error: data?.error ?? fnError?.message ?? 'Failed to create payment.' };
  }

  const { error: stripeError } = await _prStripe.confirmCardPayment(
    data.client_secret,
    { payment_method: ev.paymentMethod.id },
    { handleActions: false }
  );

  if (stripeError) { ev.complete('fail'); return { success: false, error: stripeError.message }; }

  ev.complete('success');
  return { success: true, paymentIntentId: data.payment_intent_id };
}

export function destroyPaymentRequestButton() {
  try { _prElement?.unmount(); } catch {}
  _prElement = null;
  _prRequest = null;
  _prStripe = null;
}
